import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import floorPlanImage from "../../Assets/Images/UnitSelection/Tower_B2.png";
import B2_Refuge from "../../Assets/Images/UnitSelection/B2_Refuge.png";
import { useLocation, useNavigate } from "react-router-dom";
import { Box } from "@mui/material";
import ConfirmUnitModal from "../OnlineBooking/ConfirmUnitModal";
import axios from "axios";
import { getTokenServerTime } from "../../Services/Tokens";
import EventStatusDialog from "../OnlineBooking/EventStatusDialog";

//  *  Helpers
const parseToMillis = (value) => {
  if (value == null) return null;
  if (typeof value === "number" && Number.isFinite(value)) return value;

  // numeric string?
  const num = Number(value);
  if (Number.isFinite(num)) return num;

  // ISO or date-like string?
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
};

// Unit color mapping function
const getUnitColor = (unitId, isAvailable, isBooked, isRefuge) => {
  // if (isRefuge) return 'rgba(171, 129, 255, 0.6)'; // purple for refuge

  // if (isAvailable) return 'rgba(0, 190, 70, 0.6)'; // available green
  // if (isBooked) return 'rgba(180, 180, 180, 0.5)'; // muted gray booked

  // Base color fallback for plan zones
  // if (unitId >= 1 && unitId <= 4) return 'rgba(203, 74, 74, 0.6)';
  if ((unitId >= 3 && unitId <= 8) || (unitId >= 27 && unitId <= 33))
    return "#FFD0E9";
  if ((unitId >= 1 && unitId <= 2) || (unitId >= 9 && unitId <= 10))
    return "rgb(189, 231, 255, 0.5)";
  // if (unitId === 13) return 'rgba(255, 180, 83, 0.6)';
  // if (unitId >= 14 && unitId <= 15) return 'rgba(239, 180, 193, 0.6)';
  // if (unitId >= 16 && unitId <= 21) return 'rgba(142, 189, 228, 0.6)';
  if (unitId === 11) return "#A5FDD6";
  if (unitId >= 12 && unitId <= 26) return "#FDE2A4";
  return "rgba(230, 230, 230, 0.6)";
};

const TowerB2UnitSelection = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { floorData, paymentId, tokenNo, wingId, wingName, eventId, launchId } =
    location.state || {};
  // console.log('floorData', floorData);

  const [selectedUnit, setSelectedUnit] = useState(null);
  const [hoverId, setHoverId] = useState(null);
  const [labelCenters, setLabelCenters] = useState({});
  const [units, setUnits] = useState(floorData?.units || []);
  const [serverTimeMs, setServerTimeMs] = useState(null);
  const [config, setConfig] = useState(null);
  const [eventStatusDialog, setEventStatusDialog] = useState({
    open: false,
    title: "",
    message: "",
  });

  // Prevent double navigation
  const hasNavigatedRef = useRef(false);

  /** Fetch server time */
  const fetchServerTime = useCallback(async () => {
    try {
      const res = await getTokenServerTime(); // provided by your app
      const ms = parseToMillis(res?.data?.currentTime);
      setServerTimeMs(ms);
    } catch (e) {
      console.error("Failed to fetch server time:", e);
      // Optional: fallback to client time
      // setServerTimeMs(Date.now());
    }
  }, []);

  /** Fetch online booking config */
  const fetchOnlineBookingConfig = useCallback(async () => {
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_PAYMENT_LINK_URL}customerOnlineBooking/getEventOnlineBookingConfigDetails`,
        {
          eventId: eventId,
          launchId: launchId,
        }
      );
      setConfig(res?.data?.data ?? null);
    } catch (e) {
      console.error("Failed to fetch booking config:", e);
    }
  }, [eventId, launchId]);

  /** Load both, in parallel */
  useEffect(() => {
    (async () => {
      await Promise.allSettled([fetchServerTime(), fetchOnlineBookingConfig()]);
    })();
  }, [fetchServerTime, fetchOnlineBookingConfig]);

  /** Evaluate guards when data is ready */
  useEffect(() => {
    if (serverTimeMs == null || !config) return;

    // 1) Check acceptance flag first
    if (config.isOnlineBookingAccept === false) {
      setEventStatusDialog({
        open: true,
        title: "Online Booking Disabled",
        message:
          "Online booking is currently not being accepted for this event. You’ll be redirected to the Tokens page.",
      });
      return;
    }

    // 2) Time window checks
    const startMs = parseToMillis(config.startDate);
    // const startMs = 1761656086200;
    const endMs = parseToMillis(config.endDate);
    // const endMs = 1761456086200;

    if (startMs != null && serverTimeMs < startMs) {
      setEventStatusDialog({
        open: true,
        title: "Event Yet to Start",
        message:
          "This event hasn’t started yet. Please check back later. You’ll be redirected to the Tokens page.",
      });
      return;
    }

    if (endMs != null && serverTimeMs > endMs) {
      setEventStatusDialog({
        open: true,
        title: "Event Ended",
        message:
          "This event has ended. Thank you for your interest. You’ll be redirected to the Tokens page.",
      });
      return;
    }

    // If we reach here, booking UI can proceed normally.
  }, [serverTimeMs, config]);

  /** Close dialog -> navigate to Tokens */
  const handleDialogClose = () => {
    setEventStatusDialog((d) => ({ ...d, open: false }));
    if (!hasNavigatedRef.current) {
      hasNavigatedRef.current = true;
      navigate("/Home/Tokens");
    }
  };

  const floorNo = floorData?.floorNo.split(" ")[1] || "";

  const refugeUnits = [802, 1302, 1802, 2302]; // only these are labeled "Refuge"
  const refugeFloors = [8, 13, 18, 23]; // floors where these exist

  const availableUnits = useMemo(() => {
    return units
      .filter((u) => {
        if (!u.unitId || !u.unitId.unitNo) return false;

        let numericUnitNo = u.unitId.unitNo.replace(/^[A-Za-z0-9]+-/, "");

        return (
          u.unitId.inventoryStatusId ===
            (process.env.REACT_APP_ENVIRONMENT === "PRODUCTION"
              ? "91c9faab-5adc-4b2f-a36e-b19160049413"
              : "e2c12f90-0240-49d5-b31e-177a8c836ecc") && numericUnitNo !== ""
        );
      })
      .map((u) => Number(u.unitId.unitNo.replace(/^[A-Za-z0-9]+-/, "")))
      .sort((a, b) => a - b);
  }, [units]);

  // filter available and booked based on units.unitId.inventoryStatusId (""=not available, "abcde"=available)
  // const availableUnits = units
  //   .filter((u) => {
  //     if (!u.unitId || !u.unitId.unitNo) return false;

  //     // Remove prefix (ex: A1-2101 → 2101)
  //     let numericUnitNo = u.unitId.unitNo.replace(/^[A-Za-z0-9]+-/, "");

  //     return (
  //       u.unitId.inventoryStatusId ===
  //         (process.env.REACT_APP_ENVIRONMENT === "PRODUCTION"
  //           ? "91c9faab-5adc-4b2f-a36e-b19160049413"
  //           : "e2c12f90-0240-49d5-b31e-177a8c836ecc") &&
  //       // "e2c12f90-0240-49d5-b31e-177a8c836ecc"
  //       // &&
  //       u.unitId.inventoryStatusId.trim() !== "" &&
  //       numericUnitNo !== "" // ensure valid
  //     );
  //   })
  //   // Convert numericUnitNo to a number while sorting
  //   .map((u) => Number(u.unitId.unitNo.replace(/^[A-Za-z0-9]+-/, "")))
  //   .sort((a, b) => a - b);

  // console.log("Available Units:", availableUnits);

  // const bookedUnits = units
  //   .filter((u) => {
  //     if (!u.unitId || !u.unitId.unitNo) return false;

  //     // Remove prefix (ex: A1-2101 → 2101)
  //     let numericUnitNo = u.unitId.unitNo.replace(/^[A-Za-z0-9]+-/, "");

  //     return (
  //       u.unitId.inventoryStatusId ===
  //       // "d536a7ce-8a8f-4de1-be73-7b58cfe01a65" &&
  //       (process.env.REACT_APP_ENVIRONMENT === "PRODUCTION"
  //         ? "91c9faab-5adc-4b2f-a36e-b19160049413"
  //         : "d536a7ce-8a8f-4de1-be73-7b58cfe01a65") &&
  //       u.unitId.inventoryStatusId.trim() !== "" &&
  //       numericUnitNo !== "" // ensure valid
  //     );
  //   })
  //   .map((u) => Number(u.unitId.unitNo.replace(/^[A-Za-z0-9]+-/, "")))
  //   .sort((a, b) => a - b);

  // console.log("bookedUnits", bookedUnits);
  const bookedUnits = useMemo(() => {
    return units
      .filter((u) => {
        if (!u.unitId || !u.unitId.unitNo) return false;

        let numericUnitNo = u.unitId.unitNo.replace(/^[A-Za-z0-9]+-/, "");

        return (
          u.unitId.inventoryStatusId ===
          (process.env.REACT_APP_ENVIRONMENT === "PRODUCTION"
            ? "91c9faab-5adc-4b2f-a36e-b19160049413"
            : "d536a7ce-8a8f-4de1-be73-7b58cfe01a65")
        );
      })
      .map((u) => Number(u.unitId.unitNo.replace(/^[A-Za-z0-9]+-/, "")))
      .sort((a, b) => a - b);
  }, [units]);

  // Your exported multi-path "d" from Figma
  const RAW_D = `
  M687.6651 359.489502C673.6651 377.4895 642.6651 387.4895 642.6651 387.4895L659.6651 474.489V480.489H704.6651V466.489L757.665 429.489L687.6651 359.489502Z
  M642.5589 470.847352L622.0889 381.847352C623.0889 392.847352 575.0889 386.847352 575.0889 386.847352L554.0889 410.847352V475.847352H567.0889V481.847352H598.0889V470.847352H642.5589Z
M402.5889 480.235352V386.235352H512.5889L535.5889 412.5V474.5H506.5889V469.5V455.5H500.5889V480.235352H402.5889Z

M516.5889 279.5H403.5889V367.5H512.5L535.5 364.5V295.5L516.5889 288.5V279.5Z
M605.5889 277.5C591.5 296.7646 558.5 296.7646 558.5 296.7646V367.5C632.5 388.5 671.5 343.5 671.5 343.5L605.5889 277.5Z
`;

  // 1) Split multi-subpath into separate <path>s
  // This regex grabs each subpath starting with 'M' up to (but not including) the next 'M'
  const subpaths = useMemo(() => {
    const parts = RAW_D.match(/M[^M]+/g) || [];
    return parts.map((d, i) => ({
      id: i + 1,
      name: `Unit ${i + 1}`,
      d: d.trim(),
    }));
  }, [RAW_D]);

  // ✅ Dynamically calculate text center for each path
  useEffect(() => {
    const centers = {};
    const leftSideUnits = [
      3, 5, 7, 9, 12, 14, 16, 18, 19, 21, 23, 25, 27, 29, 31, 33,
    ];
    const rightSectionUnits = [
      1, 2, 3, 4, 5, 6, 7, 8, 11, 28, 29, 30, 31, 32, 33,
    ];

    subpaths.forEach((p) => {
      const el = document.getElementById(`path-${p.id}`);
      if (el) {
        const box = el.getBBox();

        const isLeftSide = leftSideUnits.includes(p.id);
        const isRightSection = rightSectionUnits.includes(p.id);

        let visualCenterX = box.x + box.width / 2;
        let visualCenterY = box.y + box.height / 2;

        // 🧭 HORIZONTAL ADJUSTMENT
        // Each section & side has a slightly different “visual” offset
        // (because bounding boxes aren’t true geometric centers for complex shapes)

        if (isRightSection) {
          if (isLeftSide) {
            visualCenterX -= box.height * 0.12;
          } else {
            visualCenterX += box.height * 0.08;
          }
        } else {
          if (isLeftSide) {
            visualCenterX = visualCenterX - box.height * 0.08;
          } else {
            visualCenterX = visualCenterX + box.height * 0.125;
          }
        }
        if (p.id === 10) {
          visualCenterY += box.height * 0.12;
        }

        centers[p.id] = { x: visualCenterX, y: visualCenterY };
      }
    });
    setLabelCenters(centers);
  }, [subpaths]);

  const handleUnitSelection = (unitNumber) => {
    console.log("handleUnitSelection", unitNumber);

    // Find the matching unit
    const selectedUnit = units.find((u) => {
      if (!u.unitId?.unitNo) return false;

      const numericUnitNo = u.unitId.unitNo.replace(/^[A-Za-z0-9]+-/, "");
      console.log(`Comparing ${numericUnitNo} with ${unitNumber}`);

      return numericUnitNo === `${unitNumber}`;
    });

    // ❌ If NO unit found → show dialog and STOP
    if (!selectedUnit) {
      setEventStatusDialog({
        open: true,
        title: "Unit Not Available",
        message: "No data found for this unit on the selected floor.",
      });
      return; // STOP navigation
    }

    // ✅ Valid unit → attach extra data
    const enrichedUnit = {
      ...selectedUnit,
      paymentId,
      tokenNo,
    };

    console.log("selectedUnit", enrichedUnit);

    // ✅ Navigate only when unit is valid
    navigate("/Home/singleUnitSelectionDetails", {
      state: { paymentId, tokenNo, selectedUnit: enrichedUnit },
    });
  };

  // Custom hook to detect mobile view based on window width
  function useIsMobile(breakpoint = 768) {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= breakpoint);

    useEffect(() => {
      const handleResize = () => setIsMobile(window.innerWidth <= breakpoint);
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }, [breakpoint]);

    return isMobile;
  }

  // Usage:
  const isMobile = useIsMobile();

  useEffect(() => {
    let intervalId;
    const fetchUnits = async () => {
      try {
        const res = await axios.post(
          `${process.env.REACT_APP_PAYMENT_LINK_URL}customerOnlineBooking/getAllUnitsForFloorFromLaunchesForCustomerFastBooking`,
          {
            wingName: wingName, //"A1",
            wingId: wingId, //"25a202bc-a854-4b39-a07d-4322d1c1a7fe",
            floorNo: floorData?.floorNo, //"Floor 5",
          }
        );
        // console.log('res', res.data.data);
        setUnits(res.data.data);
      } catch (err) {
        // Handle error
        console.error("Error fetching units:", err);
      }
    };
    if (floorData?.floorNo) {
      fetchUnits();
      intervalId = setInterval(fetchUnits, 60000); // fetch every 60 seconds
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [!!selectedUnit, floorData?.floorNo]);

  // Helper function to format currency in Indian Rupees with Lakhs and Crores
  function formatIndianCurrency(amount) {
    if (!amount) return "N/A";

    // If amount >= 1 crore (1 Cr = 1 Crore = 10000000)
    if (amount >= 10000000) {
      return `₹ ${(amount / 10000000).toFixed(2)} Cr`;
    }

    // Otherwise show in Lakhs
    return `₹ ${(amount / 100000).toFixed(2)} L`;
  }

  const isRefugeFloor = refugeFloors.includes(Number(floorNo));

  return (
    <Box sx={{ width: "100%", overflow: `${!isMobile && "auto"}` }}>
      <Box
        onClick={() => navigate("/Home/Tokens")}
        sx={{
          display: "inline-block",
          fontSize: { xs: 14, sm: 14 },
          padding: 3,
          fontWeight: 600,
          textTransform: "uppercase",
          background: "linear-gradient(90deg, #0072ff 0%, #00b4e6ff 100%)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          cursor: "pointer",
          position: "relative",
          letterSpacing: 0.5,
          "&::after": {
            content: '""',
            position: "absolute",
            left: 0,
            bottom: -3,
            width: "100%",
            height: 2,
            background: "linear-gradient(90deg, #0072ff, #00b4e6ff)",
            transform: "scaleX(0)",
            transformOrigin: "right",
            transition: "transform 0.3s ease",
          },
          "&:hover::after": {
            transform: "scaleX(1)",
            transformOrigin: "left",
          },
        }}
      >
        ← Go Back To Token
      </Box>

      {/* ✅ Guaranteed animations injected directly into DOM */}
      <style>{`
               /* Pulse effect for AVAILABLE units */
               @keyframes borderPulse {
                 0% {
                   stroke: rgba(27, 77, 45, 0.8);
                   stroke-width: 1.5;
                   filter: drop-shadow(0 0 2px rgba(0,190,70,0.2));
                 }
                 50% {
                   stroke: rgba(1, 114, 43, 1);
                   stroke-width: 2.5;
                   filter: drop-shadow(0 0 8px rgba(0,190,70,0.45));
                 }
                 100% {
                   stroke: rgba(27, 77, 45, 0.8);
                   stroke-width: 1.5;
                   filter: drop-shadow(0 0 2px rgba(0,190,70,0.2));
                 }
               }

               /* Pulse for selected (blue outline) */
               @keyframes borderSelected {
                 0% {
                   stroke: rgba(21,112,239,0.8);
                   stroke-width: 2;
                   filter: drop-shadow(0 0 4px rgba(21,112,239,0.4));
                 }
                 50% {
                   stroke: rgba(21,112,239,1);
                   stroke-width: 4;
                   filter: drop-shadow(0 0 10px rgba(21,112,239,0.6));
                 }
                 100% {
                   stroke: rgba(21,112,239,0.8);
                   stroke-width: 2;
                   filter: drop-shadow(0 0 4px rgba(21,112,239,0.4));
                 }
               }
              @keyframes chipGlow {
                 0%, 100% {
                   opacity: 1;
                   filter: brightness(1);
                 }
                 50% {
                   opacity: 0.85;
                   filter: brightness(1.2);
                 }
               }
    `}</style>
      <Box
        sx={{
          width: isMobile ? "80vh" : "100%",
          height: isMobile ? "100vw" : "auto",
          // height: '100%',
          mx: "auto",
          overflow: `${isMobile && "scroll"}`,
          // display: 'flex',
          // justifyContent: 'center',
          // alignItems: 'center',
          background: "rgb(255 255 255 / 0%)",
          paddingTop: 6,
          transform: isMobile
            ? "rotate(0deg) translateY(0%) translateX(0%)"
            : "none",
          transformOrigin: "center center",
          transition: "transform 0.4s ease-in-out",
          // position: 'absolute',
        }}
      >
        {/* One SVG that contains BOTH the raster image and vector paths
          -> guarantees perfect alignment because they share the same viewBox */}
        <svg
          // width="1211"
          // height="513"
          // width="100%"
          // height="100%"
          width={isMobile ? "110vh" : "100%"}
          height={isMobile ? "110vw" : "100%"}
          viewBox="0 0 1263 557"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <image
            href={isRefugeFloor ? B2_Refuge : floorPlanImage}
            x="-27"
            y="-26"
            width="100%"
            height="100%"
            preserveAspectRatio="xMidYMid meet"
          />
          {subpaths.map((p) => {
            // console.log("subpath", p);

            // Calculate unit number
            const unitNumber = Number(
              `${floorNo}${String(p.id).padStart(2, "0")}`
              // Ex: floorNo=21, p.id=3 -> 2103
            );
            // console.log("units", units);
            // console.log("unitNumber", unitNumber);
            // console.log("floorNo --->", floorNo);

            // code to display carpet area
            let carpetArea = units.find((u) => {
              // Check if unitNo exists before proceeding
              if (!u.unitId || !u.unitId.unitNo) {
                // console.log("unitNo is null or undefined for unit:", u);
                return false; // Skip this iteration if unitNo is missing
              }

              let numericUnitNo = u.unitId.unitNo.replace(/^[A-Za-z0-9]+-/, ""); // Remove the prefix and dash
              // console.log(`Comparing ${numericUnitNo} with ${unitNumber}`);
              // Log the comparison
              return numericUnitNo === `${unitNumber}`;
            });

            // console.log("carpetArea", carpetArea);

            const carpetAreaInSqFt = carpetArea
              ? Math.floor(Number(carpetArea?.unitId?.chargeableArea) * 10.7639)
              : 0; // Convert to sqft

            // console.log("carpetAreaInSqFt", carpetAreaInSqFt);

            // Total cost in Indian Rupees format and in lakhs

            // Extract total cost (with INR formatting)
            let totalCost = selectedUnit?.unitId?.totalCost || 0; // Default to 12,500,000 INR if not found

            // console.log("totalCost", totalCost);

            // Format using helper function
            let price = formatIndianCurrency(totalCost);

            // console.log("Total Cost:", price);
            //
            const isHover = hoverId === p.id && !isMobile;
            const isSelected =
              Number(selectedUnit?.unitId.unitNo) === unitNumber;

            const center = labelCenters[p.id] || { x: 0, y: 0 };

            const isRefuge =
              refugeUnits.includes(unitNumber) &&
              refugeFloors.includes(Number(floorNo));
            const isAvailable = availableUnits.includes(unitNumber);
            const isBooked = bookedUnits.includes(unitNumber);

            // console.log("isAvailable", isAvailable);

            const baseColor = getUnitColor(
              p.id,
              isAvailable,
              isBooked,
              isRefuge
            );

            const notAvailable = isBooked || isRefuge;

            // console.log("not available", notAvailable);

            const labelY = center ? center.y : 0;
            const chipY = labelY + 15; // chip below number

            const statusColor =
              isAvailable && !isRefuge
                ? "rgb(77, 145, 2)"
                : isBooked
                ? "rgb(181, 24, 1)"
                : isRefuge
                ? "rgba(171,129,255,0.8)"
                : "rgba(200,200,200,0.6)";

            return (
              <g key={p.id}>
                <path
                  key={p.id}
                  id={`path-${p.id}`}
                  d={p.d}
                  stroke="black"
                  strokeWidth={isSelected ? 3 : isHover ? 1 : 0}
                  fill={
                    // notAvailable
                    //   ? 'rgba(184, 184, 184, 1)' // booked fill (greyed out)
                    //   :
                    isSelected
                      ? "rgba(21,112,239,0.45)" // selected fill (brand blue)
                      : isHover
                      ? "rgba(138, 255, 134, 0.3)" // hover fill
                      : //  ? 'rgba(21,112,239,0.25)' // hover fill
                        // : 'rgba(138, 255, 134, 0.3)'
                        "rgba(0, 0, 0, 0.02)"
                  }
                  // fill={getUnitColor(p.id, isAvailable, isBooked, isRefuge)}
                  // Ensure pointer events hit even if fill is transparent / path not closed
                  style={{
                    cursor: notAvailable ? "" : "pointer",
                    pointerEvents: notAvailable ? "none" : "all",
                    transition: "all 0.25s ease-out",
                    transformBox: "fill-box",
                    transformOrigin: "center",
                    transform: isSelected
                      ? "scale(1.05)" // slight lift when selected
                      : isHover
                      ? "scale(1.2)" // gentle zoom on hover
                      : "scale(1)",
                    filter: notAvailable
                      ? "grayscale(0.5) brightness(0.9)"
                      : "none",
                    opacity: notAvailable ? 0.75 : 1,
                    // animation:
                    //   isAvailable && !isRefuge
                    //     ? 'borderPulse 2s ease-in-out infinite'
                    //     : isSelected
                    //     ? 'borderSelected 2s ease-in-out infinite'
                    //     : isRefuge
                    //     ? 'softGlow 4s ease-in-out infinite'
                    //     : 'none',
                  }}
                  onMouseEnter={() => setHoverId(p.id)}
                  onMouseLeave={() => setHoverId(null)}
                  onClick={() =>
                    setSelectedUnit(handleUnitSelection(unitNumber))
                  }
                />

                {center && (
                  <>
                    {!isRefuge && (
                      <>
                        {/* === OUTER BORDER CONTAINER === */}
                        <rect
                          x={center.x - 20.5} // horizontal offset
                          y={center.y - 25} // vertical offset
                          width={41}
                          height={55}
                          rx={3}
                          fill={statusColor}
                          stroke="#000"
                          strokeWidth="1"
                          pointerEvents="none"
                          transform={
                            isMobile
                              ? `rotate(-90, ${center.x}, ${center.y})`
                              : undefined
                          }
                          style={{
                            animation:
                              isAvailable && !isRefuge
                                ? "chipGlow 0.5s ease-in-out infinite"
                                : "none",
                          }}
                        />
                      </>
                    )}

                    {/* === MAIN UNIT NUMBER === */}
                    <text
                      x={center.x}
                      y={isRefuge ? center.y : center.y - 15}
                      textAnchor="middle"
                      alignmentBaseline="middle"
                      fontSize={isRefuge ? "10" : "12"}
                      fontWeight="900"
                      fill={isRefuge ? "#6F42C1" : "#fff"}
                      transition="all 0.25s ease-out"
                      pointerEvents="none"
                      transform={
                        isMobile
                          ? `rotate(-90, ${center.x}, ${center.y})`
                          : undefined
                      }
                    >
                      {isRefuge
                        ? ""
                        : floorNo + String(p.id).padStart(2, "0")}
                    </text>

                    {!isRefuge && (
                      <>
                        {/* === DIVIDER LINE BELOW UNIT NUMBER === */}
                        <line
                          x1={center.x - 16}
                          y1={center.y - 8}
                          x2={center.x + 16}
                          y2={center.y - 8}
                          stroke="#fff"
                          strokeWidth="0.8"
                          pointerEvents="none"
                          transform={
                            isMobile
                              ? `rotate(-90, ${center.x}, ${center.y})`
                              : undefined
                          }
                        />

                        {/* === AREA === */}
                        <text
                          x={center.x}
                          y={center.y - 2}
                          textAnchor="middle"
                          alignmentBaseline="middle"
                          fontSize="7"
                          fontWeight="900"
                          fill="#fff"
                          pointerEvents="none"
                          transform={
                            isMobile
                              ? `rotate(-90, ${center.x}, ${center.y})`
                              : undefined
                          }
                        >
                          {carpetAreaInSqFt} Sq Ft
                        </text>

                        {/* === PRICE CHIP (Green box) === */}
                        <rect
                          x={center.x - 20}
                          y={center.y + 4}
                          // rx={3}
                          width={40}
                          height={12}
                          // fill={
                          //   isAvailable
                          //     ? '#44B564'
                          //     : isBooked
                          //     ? '#D32F2F'
                          //     : '#555'
                          // }
                          fill="#fff"
                          pointerEvents="none"
                          transform={
                            isMobile
                              ? `rotate(-90, ${center.x}, ${center.y})`
                              : undefined
                          }
                        />
                        <text
                          x={center.x}
                          y={center.y + 12}
                          textAnchor="middle"
                          fontSize="8"
                          fontWeight="600"
                          fill="black"
                          pointerEvents="none"
                          transform={
                            isMobile
                              ? `rotate(-90, ${center.x}, ${center.y})`
                              : undefined
                          }
                        >
                          {!isBooked && !isRefuge ? price : 0}
                        </text>
                      </>
                    )}

                    {/* === AVAILABILITY STATUS === */}
                    <text
                      x={center.x}
                      y={center.y + 23}
                      textAnchor="middle"
                      alignmentBaseline="middle"
                      fontSize="6"
                      fontWeight="400"
                      // fill={
                      //   isAvailable ? '#44B564' : isBooked ? '#D32F2F' : '#555'
                      // }
                      fill="#fff"
                      pointerEvents="none"
                      transform={
                        isMobile
                          ? `rotate(-90, ${center.x}, ${center.y})`
                          : undefined
                      }
                      style={{
                        animation:
                          isAvailable && !isRefuge
                            ? "chipGlow 1s ease-in-out infinite"
                            : "none",
                      }}
                    >
                      {isAvailable && !isRefuge
                        ? "AVAILABLE"
                        : isBooked
                        ? "SOLD"
                        : ""}
                    </text>
                  </>
                )}
              </g>
            );
          })}
        </svg>
      </Box>
      {/* Centered floor label */}
      {/* <Box
        sx={{
          position: 'absolute',
          bottom: isMobile ? '15%' : '6%',
          left: '50%',
          transform: 'translateX(-50%)',
          textAlign: 'center',
          fontWeight: 700,
          fontSize: isMobile ? '16px' : '22px',
          color: '#000',
          letterSpacing: '0.5px',
          textTransform: 'uppercase',
        }}>
        YOU ARE WATCHING FLOOR : {floorNo}
      </Box> */}

      {/* Fixed compass / north icon */}
      <Box
        sx={{
          position: "fixed",
          top: isMobile ? "8vh" : "10vh",
          right: isMobile ? "8vw" : "4vw",
          width: 64,
          height: 64,
          borderRadius: "50%",
          background:
            "radial-gradient(circle at 30% 30%, #fff 0%, #f1f1f1 100%)",
          boxShadow: "0 4px 10px rgba(0,0,0,0.25)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000,
          transform: isMobile ? "rotate(90deg)" : "none",
        }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 100 100"
          width="64"
          height="64"
        >
          {/* Outer circle */}
          <circle
            cx="50"
            cy="50"
            r="46"
            fill="white"
            stroke="#333"
            strokeWidth="3"
          />

          {/* North arrow (triangle) */}
          <polygon
            points="50,40 63,80 50,67 37,80"
            fill="#e53935" // red arrow
            stroke="#b71c1c"
            strokeWidth="2"
          />

          {/* 'N' label */}
          <text
            x="50"
            y="25"
            textAnchor="middle"
            fontSize="26"
            fontWeight="700"
            fill="#111"
            alignmentBaseline="middle"
          >
            N
          </text>
        </svg>
      </Box>

      {/* {selectedUnit && (
        <ConfirmUnitModal
          isOpen={!!selectedUnit} // Open modal if selectedUnit is set
          onClose={() => setSelectedUnit(null)} // Close the modal
          unitData={selectedUnit} // Pass selectedUnit data to the modal
          paymentId={paymentId}
          tokenNo={tokenNo}
        />
      )} */}
      <EventStatusDialog
        open={eventStatusDialog.open}
        title={eventStatusDialog.title}
        message={eventStatusDialog.message}
        actionLabel="Go to Tokens"
        onClose={handleDialogClose}
      />
    </Box>
  );
};

export default TowerB2UnitSelection;
